<script setup lang="ts">
import type { MigrationStepDisplaySection } from '#layers/data-model/utils/migration-step-display';
import type {
	MigrationStepGroup,
	MigrationStepTone,
} from '#layers/data-model/utils/migration-step-groups';

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
	countMigrationSteps,
	getMigrationStepDisplaySections,
	MIGRATION_STEP_TONE_DOT_COLORS,
} from '#layers/data-model/utils/migration-step-display';
import { MIGRATION_STEP_TONE_ORDER } from '#layers/data-model/utils/migration-step-groups';
import MigrationStepRow from './StepRow.vue';

interface MigrationStepDisplaySectionBlock {
	id: 'collections' | 'schema';
	labelKey: string;
	sections: MigrationStepDisplaySection[];
}

const props = defineProps<{
	groups: MigrationStepGroup[];
}>();

const COLLAPSE_BY_DEFAULT_STEP_COUNT = 12;
const COLLAPSE_BY_DEFAULT_SECTION_COUNT = 4;

const { t } = useI18n();
const activeTones = ref<Set<MigrationStepTone>>(new Set());
const openSectionIds = ref<Set<string>>(new Set());
const totalCounts = computed(() => countMigrationSteps(props.groups.flatMap((group) => group.steps)));
const totalCount = computed(() => MIGRATION_STEP_TONE_ORDER.reduce((count, tone) => count + totalCounts.value[tone], 0));
const allSections = computed(() => getMigrationStepDisplaySections(props.groups, new Set()));
const visibleSections = computed(() => activeTones.value.size === 0
	? allSections.value
	: getMigrationStepDisplaySections(props.groups, activeTones.value));
const sectionBlocks = computed<MigrationStepDisplaySectionBlock[]>(() => [
	{
		id: 'collections' as const,
		labelKey: 'pages.data_model.introspection.matrix.section_titles.collections',
		sections: visibleSections.value.filter((section) => section.groupId === 'collection'),
	},
	{
		id: 'schema' as const,
		labelKey: 'pages.data_model.introspection.matrix.section_titles.schema',
		sections: visibleSections.value.filter((section) => section.groupId !== 'collection'),
	},
].filter((block) => block.sections.length > 0));
const visibleFilterTones = computed(() => MIGRATION_STEP_TONE_ORDER.filter((tone) => totalCounts.value[tone] > 0));
const hasCollapsedSections = computed(() => allSections.value.some((section) => !isSectionOpen(section.id)));
const hasOpenSections = computed(() => allSections.value.some((section) => isSectionOpen(section.id)));

watch(() => props.groups, () => {
	openSectionIds.value = getDefaultOpenSectionIds(allSections.value);
}, { immediate: true });

function clearToneFilter() {
	activeTones.value = new Set();
}

function toggleToneFilter(tone: MigrationStepTone) {
	const next = new Set(activeTones.value);

	if (next.has(tone)) {
		next.delete(tone);
	}
	else {
		next.add(tone);
	}

	activeTones.value = next;
}

function isToneActive(tone: MigrationStepTone) {
	return activeTones.value.has(tone);
}

function visibleSectionToneCounts(section: MigrationStepDisplaySection) {
	const counts = countMigrationSteps(section.steps);

	return MIGRATION_STEP_TONE_ORDER
		.map((tone) => ({ tone, count: counts[tone] }))
		.filter(({ count }) => count > 0);
}

function getToneFilterLabel(tone: MigrationStepTone, count: number) {
	return t(`pages.data_model.introspection.matrix.filters.${tone}`, { count });
}

function getAllFilterLabel() {
	return t('pages.data_model.introspection.matrix.filters.all', { count: totalCount.value });
}

function getDefaultOpenSectionIds(nextSections: MigrationStepDisplaySection[]) {
	const shouldCollapseByDefault = totalCount.value > COLLAPSE_BY_DEFAULT_STEP_COUNT || nextSections.length > COLLAPSE_BY_DEFAULT_SECTION_COUNT;

	if (shouldCollapseByDefault) {
		return new Set<string>();
	}

	return new Set(nextSections.map((section) => section.id));
}

function isSectionOpen(sectionId: string) {
	return openSectionIds.value.has(sectionId);
}

function setSectionOpen(sectionId: string, open: boolean) {
	const next = new Set(openSectionIds.value);

	if (open) {
		next.add(sectionId);
	}
	else {
		next.delete(sectionId);
	}

	openSectionIds.value = next;
}

function expandAllSections() {
	openSectionIds.value = new Set(allSections.value.map((section) => section.id));
}

function collapseAllSections() {
	openSectionIds.value = new Set();
}
</script>

<template>
	<div class="flex flex-col gap-5" data-testid="migration-steps">
		<div class="flex flex-wrap items-center justify-between gap-2">
			<div class="flex flex-wrap items-center gap-2">
				<DButton
					size="xs"
					color="neutral"
					:variant="activeTones.size === 0 ? 'solid' : 'outline'"
					:label="getAllFilterLabel()"
					:aria-label="getAllFilterLabel()"
					rounded
					@click="clearToneFilter"
				/>
				<DButton
					v-for="tone in visibleFilterTones"
					:key="tone"
					size="xs"
					color="neutral"
					:variant="isToneActive(tone) ? 'solid' : 'outline'"
					:label="getToneFilterLabel(tone, totalCounts[tone])"
					:aria-label="getToneFilterLabel(tone, totalCounts[tone])"
					rounded
					@click="toggleToneFilter(tone)"
				>
					<template #leading>
						<DColorDot :color="MIGRATION_STEP_TONE_DOT_COLORS[tone]" size="xs" class="me-1" :ui="{ dot: 'size-1.5' }" />
					</template>
				</DButton>
			</div>

			<div class="flex shrink-0 items-center gap-2">
				<DButton
					size="xs"
					color="neutral"
					variant="ghost"
					:label="$t('pages.data_model.introspection.matrix.expand_all')"
					:disabled="!hasCollapsedSections"
					@click="expandAllSections"
				/>
				<DButton
					size="xs"
					color="neutral"
					variant="ghost"
					:label="$t('pages.data_model.introspection.matrix.collapse_all')"
					:disabled="!hasOpenSections"
					@click="collapseAllSections"
				/>
			</div>
		</div>

		<div
			v-for="block in sectionBlocks"
			:key="block.id"
			class="flex flex-col gap-1"
			:class="block.id === 'schema' ? 'border-t border-muted pt-5' : ''"
		>
			<h3 class="text-xs font-semibold text-dimmed uppercase">
				{{ $t(block.labelKey) }}
			</h3>

			<section
				v-for="section in block.sections"
				:key="section.id"
				class="flex flex-col gap-1"
				data-testid="migration-step-group"
				:data-group-id="section.id"
			>
				<button
					type="button"
					class="
						-ms-1 flex w-[calc(100%+0.25rem)] items-center justify-between gap-4 rounded-sm py-1 pe-1 text-start
						transition-colors
						hover:bg-muted/50
						focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
					"
					:aria-expanded="isSectionOpen(section.id)"
					@click="setSectionOpen(section.id, !isSectionOpen(section.id))"
				>
					<div class="flex min-w-0 items-center gap-1.5">
						<DIcon
							name="lucide:chevron-right"
							class="size-4 shrink-0 text-muted transition-transform duration-200"
							:class="isSectionOpen(section.id) ? 'rotate-90' : ''"
						/>
						<span class="min-w-0 truncate text-sm font-semibold text-default">
							<span v-if="section.groupId === 'collection'" class="font-mono">{{ section.label }}</span>
							<span v-else>{{ $t(`pages.data_model.introspection.groups.${section.id}`) }}</span>
						</span>
					</div>

					<div class="flex shrink-0 items-center gap-1">
						<DTooltip
							v-for="{ tone, count } in visibleSectionToneCounts(section)"
							:key="tone"
							:text="getToneFilterLabel(tone, count)"
						>
							<DBadge
								color="neutral"
								variant="outline"
								size="sm"
								rounded
								:aria-label="getToneFilterLabel(tone, count)"
							>
								<template #leading>
									<DColorDot :color="MIGRATION_STEP_TONE_DOT_COLORS[tone]" size="xs" :ui="{ dot: 'size-1.5' }" />
								</template>
								<span class="font-mono">{{ count }}</span>
							</DBadge>
						</DTooltip>
					</div>
				</button>

				<DCollapsible
					:open="isSectionOpen(section.id)"
				>
					<template #content>
						<ul class="flex flex-col gap-0.5 ps-5">
							<MigrationStepRow
								v-for="step in section.steps"
								:key="step.id"
								:step
								data-testid="migration-step"
								:data-step-kind="step.kind"
							/>
						</ul>
					</template>
				</DCollapsible>
			</section>
		</div>
	</div>
</template>
